import React from 'react';

/**
 * The square stat tile used on Safety & Compliance and Safety Metrics.
 *
 * Lifted verbatim out of `SafetyScreen.tsx`, where it had already been
 * extracted from three inline copies. It moved here rather than being cloned
 * because the metrics page needs the same tile with two additions — a subtext
 * line and a neutral tone — and a second component carrying the same classes
 * is how two tile styles drift apart.
 *
 * `tone` colours the VALUE, not a judgement. There is deliberately no
 * threshold logic here: whether a TRIF of 1.8 is good is a question about the
 * customer's prequalification target, and the frontend does not know it. See
 * `neutral` below.
 */
export type StatTileTone = 'green' | 'amber' | 'red' | 'purple' | 'neutral';

export interface StatTileProps {
  value: React.ReactNode;
  label: React.ReactNode;
  icon: React.ReactNode;
  tone: StatTileTone;
  /**
   * Small muted line beneath the label — on the metrics page this carries the
   * numerator and denominator, so a rate can always be checked against the
   * figures it came from without opening another screen.
   */
  subtext?: React.ReactNode;
  /** Omitted for a tile that is not a link. Renders a div rather than a button. */
  onClick?: () => void;
}

const ring: Record<StatTileTone, string> = {
  green: 'bg-green-500/15',
  amber: 'bg-amber-500/15',
  red: 'bg-red-500/15',
  purple: 'bg-purple-500/15',
  // Neutral reads as "a figure", not as a verdict. A genuine TRIF of 0.00 is a
  // real, earned result and must not be tinted green as if the app had graded
  // it; a TRIF of 4.0 must not be tinted red for the same reason.
  neutral: 'bg-white/10',
};

const text: Record<StatTileTone, string> = {
  green: 'text-green-400',
  amber: 'text-amber-400',
  red: 'text-red-400',
  purple: 'text-purple-400',
  neutral: 'text-white',
};

export default function StatTile({ value, label, icon, tone, subtext, onClick }: StatTileProps) {
  const body = (
    <>
      <div className={`w-10 h-10 rounded-full ${ring[tone]} flex items-center justify-center mx-auto mb-2`}>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${text[tone]}`}>{value}</div>
      <div className="text-xs text-fieldiq-muted mt-1 leading-tight">{label}</div>
      {subtext && (
        <div className="text-[11px] text-fieldiq-muted mt-1.5 leading-snug">{subtext}</div>
      )}
    </>
  );

  const className = 'bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 text-center';

  if (!onClick) {
    return <div className={className}>{body}</div>;
  }

  return (
    <button onClick={onClick} className={`${className} card-hover`}>
      {body}
    </button>
  );
}
